/*
 * Trimming a reply thread down to the message that was actually just sent.
 *
 * Body.getAsync hands back the whole thread: the new message, then every
 * quoted reply beneath it, then a signature. Today that only makes a ticket
 * description longer than it needs to be. When extraction arrives it becomes a
 * correctness problem — last week's quantities sitting in the same text as
 * this week's, with nothing in the text saying which is which.
 *
 * Everything here is heuristic, and it has to be: mail clients do not agree on
 * how to mark a quote, and the three languages this add-in ships to phrase the
 * marker differently and put the sender's name on different sides of the verb.
 * What keeps the heuristic safe is the rule at the bottom of latestMessage —
 * a cut that would leave nothing behind is not a cut.
 */

/**
 * "-----Original Message-----" and its forwarded and translated variants.
 * Gmail's "---------- Forwarded message ---------" has uneven dashes, which is
 * why both sides are counted loosely.
 */
const BANNER =
  /^[ \t]*-{2,}[ \t]*(?:original message|mensaje original|ursprüngliche nachricht|forwarded message|mensaje reenviado|weitergeleitete nachricht)[ \t]*-{2,}[ \t]*$/im;

/** Apple Mail's forward marker, which carries no dashes at all. */
const APPLE_FORWARD = /^[ \t]*begin forwarded message[ \t]*:[ \t]*$/im;

/**
 * The horizontal rule Outlook on the web draws above the quoted headers. Ten
 * underscores is well past anything a person types by hand.
 */
const OUTLOOK_RULE = /^[ \t]*_{10,}[ \t]*$/m;

/**
 * A quoted header block: a From-like line with a second header line under it.
 * Both halves are required on purpose — "De:" alone opens plenty of ordinary
 * Spanish sentences, and matching it by itself would eat the message.
 */
const HEADER_BLOCK =
  /^[ \t]*(?:from|de|von)[ \t]*:[^\n]*\n(?:[^\n]*\n){0,2}?[ \t]*(?:sent|enviado|gesendet|to|para|an|subject|asunto|betreff)[ \t]*:/im;

/**
 * The attribution line clients write above a quote: "On <date> <who> wrote:",
 * "El <fecha> <quien> escribió:", "Am <Datum> schrieb <wer>:".
 *
 * English and Spanish put the name before the verb and German puts it after,
 * so text is allowed on both sides. The line may also wrap once, which is what
 * Gmail does when the sender's address is long.
 *
 * The verb is closed with a lookahead rather than \b because \b is defined
 * over [A-Za-z0-9_]: there is no word boundary between the ó of "escribió" and
 * the colon after it, so \b would reject the accented spelling — the one
 * Spanish clients actually write.
 */
const ATTRIBUTION =
  /^[ \t]*(?:on|el|am)\b[^\n]{0,200}(?:\n[^\n]{0,200})?\b(?:wrote|escribi[oó]|schrieb)(?![a-z])[^\n]{0,120}:[ \t]*$/im;

/*
 * None of these carry the g flag. A global regex keeps lastIndex between
 * calls, which would make the second call on the same text find nothing.
 */
const QUOTE_MARKERS = [BANNER, APPLE_FORWARD, OUTLOOK_RULE, HEADER_BLOCK, ATTRIBUTION];

/** RFC 3676's signature delimiter, tolerating the trailing space being eaten. */
const SIGNATURE_DELIMITER = /^[ \t]*--[ \t]*$/m;

/**
 * How much text may follow the delimiter and still be a signature. Past this,
 * the dashes were a divider inside the message, and cutting there would throw
 * away what the person actually wrote.
 */
const MAX_SIGNATURE_LINES = 8;

/** Where the quoted part of the thread starts, or -1 when nothing marks it. */
function quoteStart(text: string): number {
  let earliest = -1;
  for (const marker of QUOTE_MARKERS) {
    const match = marker.exec(text);
    if (match && (earliest === -1 || match.index < earliest)) {
      earliest = match.index;
    }
  }
  return earliest;
}

function stripSignature(text: string): string {
  const match = SIGNATURE_DELIMITER.exec(text);
  if (!match) {
    return text;
  }
  const following = text.slice(match.index + match[0].length).trim();
  const lines = following === "" ? 0 : following.split("\n").length;
  return lines > MAX_SIGNATURE_LINES ? text : text.slice(0, match.index);
}

/**
 * Returns the most recent message in a thread, without its signature.
 *
 * Falls back to the whole body when trimming would leave nothing — which
 * happens when a marker matched something that was not a quote boundary, and
 * when the body genuinely is one forwarded message and nothing else. In both
 * cases too much text beats none.
 */
export function latestMessage(body: string): string {
  const text = (body ?? "").replace(/\r\n/g, "\n").replace(/\r/g, "\n");
  const cut = quoteStart(text);
  const head = cut === -1 ? text : text.slice(0, cut);
  const trimmed = stripSignature(head).trim();
  return trimmed === "" ? text.trim() : trimmed;
}
